import { QueryClient, Query } from '@tanstack/react-query';
import { STALE_TIME } from "../utils/constants.ts";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 1,
    },
  },
});

/**
 * Creates a predicate function for invalidating queries whose key includes any of the provided strings.
 *
 * @param strings - One or more strings to check for in the query key
 * @returns A predicate function that checks if the query key includes any of the strings
 *
 * @example
 * // Invalidate queries with a single string
 * queryClient.invalidateQueries({
 *   predicate: queryKeyIncludes("/all/notes")
 * });
 *
 * @example
 * // Invalidate queries with multiple strings (OR logic)
 * queryClient.invalidateQueries({
 *   predicate: queryKeyIncludes("/all/trades", "/all/purchases")
 * });
 */
export const queryKeyIncludes = (...strings: string[]) => {
  return (query: Query) => {
    const key = query.queryKey.join("/");
    return strings.some((str) => key.includes(str));
  };
};

/**
 * Invalidates all queries whose key includes any of the provided strings.
 * This is a convenience wrapper around queryClient.invalidateQueries().
 *
 * @param strings - One or more strings to check for in the query key
 * @returns A promise that resolves when the queries have been invalidated
 *
 * @example
 * // Invalidate queries with a single string
 * invalidateQueriesByKey("/all/notes");
 *
 * @example
 * // Invalidate queries with multiple strings (OR logic)
 * invalidateQueriesByKey("/all/trades", "/all/purchases");
 *
 * @example
 * // Can be awaited if needed
 * await invalidateQueriesByKey("/all/users");
 */
export const invalidateQueriesByKey = (...strings: string[]) => {
  return queryClient.invalidateQueries({
    predicate: queryKeyIncludes(...strings),
  });
};
